import {sanitizeHtml} from "../../components/sanitizeHtml";

export interface TocEntry {
    id: string;
    text: string;
    // The h3s after an h2, until the next h2.
    children: TocEntry[];
}

// Documentation HTML, sanitized, with an id on each h2 and h3, and the contents built from them. The ids start with
// doc- so a heading can't take the name of anything else on the page. summary is the first paragraph's text ('' if
// there isn't one), for search results.
export function withHeadingIds(html: string): { html: string, toc: TocEntry[], summary: string } {
    const doc = new DOMParser().parseFromString(sanitizeHtml(html, { images: true }), 'text/html');
    const used = new Set<string>();
    const toc: TocEntry[] = [];
    for (const heading of doc.body.querySelectorAll('h2, h3')) {
        const text = (heading.textContent ?? '').replace(/\s+/g, ' ').trim();
        if (!text) {
            continue;
        }
        const id = uniqueId(`doc-${slug(text) || 'section'}`, used);
        heading.id = id;
        const entry = { id, text, children: [] };
        const parent = toc[toc.length - 1];
        // An h3 before any h2 goes at the top level.
        if (heading.tagName === 'H3' && parent) {
            parent.children.push(entry);
        } else {
            toc.push(entry);
        }
    }
    const summary = [...doc.body.querySelectorAll('p')].map(x => (x.textContent ?? '').replace(/\s+/g, ' ').trim()).find(x => x) ?? '';
    return { html: doc.body.innerHTML, toc, summary };
}

function slug(text: string): string {
    return text.normalize('NFKD').replace(/[̀-ͯ]/g, '').toLowerCase()
        .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').substring(0, 60);
}

function uniqueId(base: string, used: Set<string>): string {
    let id = base;
    for (let n = 2; used.has(id); n++) {
        id = `${base}-${n}`;
    }
    used.add(id);
    return id;
}
